import { useId } from "react";
import type { BuilderField } from "@froppio/form-core";

const boxClass = "rounded-xl border border-border bg-background";

function answerDescription(field: BuilderField) {
  switch (field.widget) {
    case "longText": return "Long text answer";
    case "number": return "Number answer";
    case "email": return "Email answer";
    case "phone": return "Phone answer";
    case "date": return "Date answer";
    case "radio": return "Choose one option";
    case "dropdown": return "Choose one option";
    case "multipleChoice": return "Choose any options";
    case "yesNo": return "Choose yes or no";
    case "signature": return "Signature";
    default: return "Short text answer";
  }
}

export function FormSheetField({ field }: { field: BuilderField }) {
  const id = useId();
  const label = field.label.trim() ? field.label : "Untitled question";
  const describedBy = [`${id}-type`, field.helpText ? `${id}-help` : undefined].filter(Boolean).join(" ");
  const multiple = field.widget === "multipleChoice";
  const choices = field.widget === "yesNo"
    ? [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]
    : field.choices;

  function answer() {
    if (field.widget === "radio" || field.widget === "dropdown" || field.widget === "multipleChoice" || field.widget === "yesNo") {
      if (!choices.length) return <p className="text-xs text-muted-foreground">No options have been added yet.</p>;
      return (
        <ul aria-label={`Options for ${label}`} className="flex flex-wrap gap-2">
          {choices.map((choice, index) => (
            <li key={`${choice.value}-${index}`} className="inline-flex min-h-9 items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-foreground">
              <span aria-hidden="true" className={`size-3 shrink-0 border-[1.5px] border-muted-foreground ${multiple ? "rounded-[4px]" : "rounded-full"}`} />
              <span className="break-words">{choice.label.trim() ? choice.label : `Untitled option ${index + 1}`}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (field.widget === "signature") {
      return (
        <div aria-hidden="true" className={`${boxClass} relative h-24`}>
          <span className="absolute bottom-3 left-3 right-3 flex items-end gap-2 text-sm text-border">
            <span>✕</span><span className="mb-1 h-px flex-1 bg-border" />
          </span>
        </div>
      );
    }
    return <div aria-hidden="true" className={`${boxClass} ${field.widget === "longText" ? "h-20" : "h-11"}`} />;
  }

  return (
    <div role="group" aria-labelledby={`${id}-label`} aria-describedby={describedBy} className="min-w-0 space-y-[7px]">
      <p id={`${id}-label`} className="break-words text-sm font-semibold text-foreground">
        {label} {field.required ? <span aria-hidden="true" className="text-destructive">*</span> : <span className="text-xs font-normal text-muted-foreground">Optional</span>}
        {field.required && <span className="sr-only"> (required)</span>}
      </p>
      <span id={`${id}-type`} className="sr-only">{answerDescription(field)}</span>
      {answer()}
      {field.helpText && <p id={`${id}-help`} className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">{field.helpText}</p>}
    </div>
  );
}
